import React, { useEffect, useRef, useState } from "react";
import { Clock3, FileUp, Mail, MapPin, Phone, X } from "lucide-react";
import { toast } from "react-toastify";
import { createSupportRequest } from "../../service/userService";
import "../../assets/css/Support.css";

const MAX_MESSAGE_LENGTH = 5000;

const Support = () => {
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [preview, setPreview] = useState("");
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!attachment) { setPreview(""); return; }
    const url = URL.createObjectURL(attachment);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  const selectFile = (files) => {
    setDragging(false);
    if (!files.length) return;
    if (files.length > 1) { setError("Please attach one screenshot at a time."); toast.warn("Please attach one screenshot at a time."); return; }
    const file = files[0];
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPG, PNG, or WEBP image."); toast.warn("Choose a JPG, PNG, or WEBP image."); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Your screenshot must be 5 MB or smaller."); toast.warn("Your screenshot must be 5 MB or smaller."); return;
    }
    setAttachment(file);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const messageLength = message.trim().length;
    if (!messageLength) { setError("Please enter a message."); toast.warn("Please enter a message."); return; }
    if (messageLength < 10) { setError("Please enter at least 10 characters to describe your issue."); toast.warn("Please enter at least 10 characters to describe your issue."); return; }
    if (messageLength > MAX_MESSAGE_LENGTH) {
      const limitMessage = `Your message must be ${MAX_MESSAGE_LENGTH.toLocaleString()} characters or fewer.`;
      setError(limitMessage);
      toast.warn(limitMessage);
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const response = await createSupportRequest(message, attachment);
      toast.success(response?.message || "Your support request has been submitted.");
      setMessage("");
      setAttachment(null);
    } catch (requestError) {
      const timedOut =
        ["ECONNABORTED", "ETIMEDOUT"].includes(requestError?.code) ||
        [408, 504].includes(requestError?.response?.status) ||
        /timed?\s*out|timeout/i.test(requestError?.message || "");
      const errorMessage =
        (timedOut && "The request timed out. Please check your connection and try again.") ||
        requestError?.response?.data?.message ||
        (!requestError?.response && "Unable to reach support. Please check your connection and try again.") ||
        "Unable to submit your support request. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-search-results support-page">
      <section className="sr-shell-card p-3 p-md-4 support-heading">
        <span className="support-heading-icon"><Phone size={21} aria-hidden="true" /></span>
        <div>
          <h1 className="sr-page-title">Support</h1>
          <p className="sr-page-subtitle mb-0">We're here to help! Choose an option below or send us a message.</p>
        </div>
      </section>

      <section className="support-contact p-3 p-md-4" aria-labelledby="support-contact-title">
        <h2 id="support-contact-title">Contact Information</h2>
        <div className="support-contact-grid">
          <div className="support-contact-item">
            <Mail size={16} aria-hidden="true" />
            <div><h3>Email</h3><a href="mailto:support@inpcommunity.com">support@inpcommunity.com</a></div>
          </div>
          <div className="support-contact-item">
            <Clock3 size={16} aria-hidden="true" />
            <div><h3>Response Time</h3><p>Within 24–48 hours</p></div>
          </div>
          <div className="support-contact-item">
            <MapPin size={16} aria-hidden="true" />
            <div><h3>Address</h3><p>UK, USA, INDIA</p></div>
          </div>
        </div>
      </section>

      <section className="sr-shell-card p-3 p-md-4">
        <div className="support-form-heading">
          <h2>Send us a message</h2>
          <p className="sr-page-subtitle mb-0">Fill in the details below and our team will get back to you soon.</p>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <label className="visually-hidden" htmlFor="support-message">Describe your issue</label>
          <textarea id="support-message" className="support-message" placeholder="Describe your issue"
            value={message}
            onChange={(event) => { setMessage(event.target.value.slice(0, MAX_MESSAGE_LENGTH)); setError(""); }}
            maxLength={MAX_MESSAGE_LENGTH} rows={4} disabled={isSubmitting}
            aria-describedby="support-message-count" />
          <p id="support-message-count" className="support-message-count" aria-live="polite">
            {message.length.toLocaleString()} / {MAX_MESSAGE_LENGTH.toLocaleString()} characters
          </p>
          <label className="support-upload-label" htmlFor="support-screenshot">Attach Screenshot (Optional)</label>
          <div className={`support-upload ${dragging ? "is-dragging" : ""}`}
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
            onDrop={(event) => { event.preventDefault(); selectFile(event.dataTransfer.files); }}>
            <input ref={fileInput} id="support-screenshot" className="visually-hidden" type="file"
              accept="image/jpeg,image/png,image/webp" aria-describedby="support-upload-help"
              disabled={isSubmitting}
              onChange={(event) => { selectFile(event.target.files); event.target.value = ""; }} />
            {attachment ? (
              <div className="support-attachment">
                {preview && <img src={preview} alt="Selected screenshot preview" />}
                <span>{attachment.name}<small>{(attachment.size / 1024 / 1024).toFixed(2)} MB</small></span>
                <button type="button" className="support-remove" aria-label="Remove screenshot" disabled={isSubmitting} onClick={() => { setAttachment(null); setError(""); }}><X size={18} /></button>
              </div>
            ) : (
              <>
                <FileUp size={30} strokeWidth={1.6} aria-hidden="true" />
                <p>Drag &amp; drop file here or <button className="support-browse" type="button" disabled={isSubmitting} onClick={() => fileInput.current.click()}>Browse</button></p>
                <small id="support-upload-help">Supported: JPG, PNG, WEBP (Max 5MB)</small>
              </>
            )}
          </div>
          {error && <p className="support-error" role="alert">{error}</p>}
          <button className="support-send" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Support;
